import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ISdFormComponentItem } from '../form-component-interface.model';
import { CustomValueAccessorComponent } from '../custom-value-accessor.component';
import { v4 as uuid } from 'uuid';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { IconSizeEnum } from '../../../_enums/icon.enum';
import { SdIconComponent } from '../../icons/icon/icon.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { SdBannerComponent } from '../../medias/banner/banner.component';

/**
 * Radio-buttons component.
 * The component is used in the following way:
 * ```
 * <sd-form radio-group-inline
 *   class="c__btn-group"
 *   id="form_modele_creneau"
 *   formControlName="creneauId"
 *   [nameField]="'creneauId'"
 *   [items]="interventionCreneaux.enumCreneau"
 *   [labelTitle]="'modele.label.creneau' | translate" >
 * </sd-form>
 * ```
 */
@Component({
  selector: 'sd-form[radio-group], sd-form[radio-group-inline]',
  templateUrl: './form-radio-group.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SdFormRadioGroupComponent,
      multi: true
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    NgClass,
    SdIconComponent,
    SdBannerComponent,
    NgTemplateOutlet,
    TranslateModule
  ]
})
export class SdFormRadioGroupComponent
  extends CustomValueAccessorComponent<ISdFormComponentItem>
  implements ControlValueAccessor
{
  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  uuidInput: string = uuid();
  currentValue: ISdFormComponentItem;
  adyenPayment: ISdFormComponentItem;
  _items: ISdFormComponentItem[];
  iconSize = IconSizeEnum;

  @HostBinding('class.is-disabled')
  @Input()
  disabled = false;

  /**
   * Label of the form element
   */
  @Input()
  labelTitle: string;

  /**
   * Allows to hide the label
   */
  @Input()
  labelHidden = false;

  /**
   * Allows to hide the label
   */
  @Input()
  applyDescriptionHidden = false;

  /**
   * Name of the form element (html attribute)
   */
  @Input()
  nameField: string;

  /**
   * Is the element mandatory?
   */
  @Input()
  required = false;

  /**
   * List of possible options
   */
  @Input()
  set items(items: ISdFormComponentItem[]) {
    this._items = items;
    const selectedItem = this._items?.find(
      (it: ISdFormComponentItem) => it.checked === true,
    );
    this.adyenPayment = this._items?.find(
      (it: ISdFormComponentItem) => it.showPaymentMethods === true,
    );
    this.writeValue(selectedItem);
  }
  get items(): ISdFormComponentItem[] {
    return this._items;
  }

  @Output()
  valueChanged = new EventEmitter<any>();

  @Input()
  set selectAdyen(value: boolean) {
    if (value) {
      this.setOption(this.adyenPayment);
    }
  }

  onKeyUp(event, item): void {
    if (event.code === 'Enter' && !item.disabled) {
      this.setOption(item);
    }
  }

  /**
   * Modifies the currently selected option
   */
  setOption(selectedItem: ISdFormComponentItem): void {
    this.items?.forEach((item) => {
      if (item?.checked != null) {
        item.checked = false;
        item.showDescription = false;
      }
    });
    if (selectedItem?.checked != null) {
      selectedItem.checked = true;
    }
    this.writeValue(selectedItem);
    this.propagateChange(selectedItem);
    this.valueChanged.emit(selectedItem);
  }

  /**
   * Allows angular to update the model
   * @param selectedItem items to be selected
   */
  writeValue(selectedItem: ISdFormComponentItem): void {
    if (selectedItem) {
      this._resetChecked();
      this.currentValue = selectedItem;
      this._checkItem(selectedItem);
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Allows angular to disable the component.
   * Currently unmanaged.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private _resetChecked(): void {
    this._items.forEach((currItem) => (currItem.checked = false));
  }

  private _checkItem(selectedItem: ISdFormComponentItem): void {
    if (!selectedItem) {
      return;
    }
    const item = this._items.find((it) => it.value === selectedItem.value);
    if (item) {
      item.checked = true;
    }
  }
}
